import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useHistory } from "react-router-dom";
import { HiOutlineTrash, HiOutlinePlus, HiInformationCircle } from "react-icons/hi";
import { MdDragIndicator } from "react-icons/md";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import API from "../../api";
import CodeLookup from "../../components/CodeLookup";
import ExamTypeSelect from "../../components/action/ExamTypeSelect";
import QuestionLookup from "../../components/QuestionLookup";
import AnswerLookup from "../../components/AnswerLookup";
import Trigger from "../../components/question/Trigger";
import DependencyExplorer from "../../components/dependencies/DependencyExplorer";
import { capture } from "../../instrument";
import useReorderable from "../../hooks/useReorderable";
import { EFFECT_TYPES } from "../../utils";

const TARGETABLE_TYPES = ["ADD_CODES", "REMOVE_CODES", "REPLACE_CODES", "REMOVE_ALL_CODES_EXCEPT", "REMOVE_CODES_OF_TYPE", "REMOVE_ALL_CODES_EXCEPT_OF_TYPE", "ANSWER_QUESTION"];

const sanitizeTargets = (list) =>
  list.map((e, idx) => {
    if (!e.target) return e;
    const valid = list.slice(0, idx).some((x) => x.type === "DUPLICATE_ADMISSION" && x.options?.duplicateName === e.target);
    return valid ? e : { ...e, target: null };
  });

const AddAction = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [groupsOperator, setGroupsOperator] = useState("AND");
  const [mode, setMode] = useState("edge");
  const [triggerGroups, setTriggerGroups] = useState([{ operator: "AND", triggers: [{ type: "CODE_SOME", values: [], field: "" }] }]);
  const hasStatusTrigger = triggerGroups.some((group) => (group.triggers || []).some((trigger) => trigger.type === "STATUS"));
  const effectiveMode = hasStatusTrigger ? "once" : mode;
  const [effects, setEffects] = useState([{ type: "ADD_CODES", values: [] }]);
  const [openAutocompleteIndex, setOpenAutocompleteIndex] = useState(null);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const { getDragProps, isDragging, showDropBefore, showDropAfter } = useReorderable(effects, (newEffects) => setEffects(sanitizeTargets(newEffects)));

  const { data: diagramData } = useQuery({
    queryKey: ["diagramData"],
    queryFn: async () => {
      const [questions, guidelines, actions] = await Promise.all([API.get("/question"), API.get("/guideline"), API.get("/action")]);
      if (!questions.ok || !guidelines.ok || !actions.ok) throw new Error("Impossible de charger les dépendances");
      return { questions: questions.data, guidelines: guidelines.data, actions: actions.data };
    },
  });

  const createMutation = useMutation({
    mutationFn: (body) => API.post("/action", body),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Action ajoutée avec succès");
      queryClient.invalidateQueries({ queryKey: ["actions"] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
      history.push("/action");
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const addAction = () => {
    if (!name.trim()) return toast.error("Veuillez saisir un nom pour l'action");
    const hasTriggers = triggerGroups.some((group) => group.triggers && group.triggers.length > 0);
    if (!hasTriggers) return toast.error("Veuillez ajouter au moins un déclencheur");
    const validEffects = effects.filter((e) => e.type);
    if (validEffects.length === 0) return toast.error("Veuillez ajouter au moins un effet");
    createMutation.mutate({ name: name.trim(), mode: effectiveMode, groupsOperator, triggerGroups, effects: validEffects });
  };

  const addEffect = () => {
    setEffects([...effects, { type: "ADD_CODES", values: [] }]);
  };

  const removeEffect = (index) => {
    if (effects.length <= 1) return toast.error("Une action doit avoir au moins un effet");
    setEffects(sanitizeTargets(effects.filter((_, i) => i !== index)));
  };

  const updateEffectType = (index, newType) => {
    const effectConfig = EFFECT_TYPES.find((e) => e.value === newType);
    const updated = effects.map((e, i) => {
      if (i !== index) return e;
      const options =
        newType === "DUPLICATE_ADMISSION"
          ? { keepAnswers: false, followsParent: false }
          : newType === "REPLACE_CODES"
            ? { replaceWith: e.options?.replaceWith || [] }
            : newType === "ANSWER_QUESTION"
              ? { questionId: "", answer: "", answerType: "" }
              : {};
      const next = { type: newType, values: effectConfig?.hasValues ? e.values || [] : [], options };
      if (TARGETABLE_TYPES.includes(newType) && e.target) next.target = e.target;
      return next;
    });
    setEffects(sanitizeTargets(updated));
  };

  const updateEffectValues = (index, values) => {
    setEffects(effects.map((e, i) => (i === index ? { ...e, values } : e)));
  };

  const updateEffectTarget = (index, target) => {
    setEffects(effects.map((e, i) => (i === index ? { ...e, target: target || null } : e)));
  };

  const updateEffectDuplicateName = (index, duplicateName) => {
    setEffects(effects.map((e, i) => (i === index ? { ...e, options: { ...e.options, duplicateName: duplicateName || "" } } : e)));
  };

  const getEarlierDuplicateNames = (currentIndex) =>
    effects
      .slice(0, currentIndex)
      .filter((e) => e.type === "DUPLICATE_ADMISSION" && e.options?.duplicateName)
      .map((e) => e.options.duplicateName);

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-gray-900">Ajouter une action</h1>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <button
            type="button"
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
            onClick={() => history.push("/action")}
          >
            Annuler
          </button>
          <button
            type="button"
            className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            onClick={addAction}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Chargement..." : "Ajouter"}
          </button>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-5 overflow-hidden">
        <div className="col-span-3 overflow-y-auto">
          <div className="mx-auto max-w-4xl px-8 py-6">
            <h3 className="mb-4 text-lg font-medium text-gray-900">Informations</h3>
            <div className="mb-6 rounded-lg border border-gray-100 bg-white p-5">
              <label className="mb-1 block text-sm font-medium text-gray-700">Nom de l'action</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Dupliquer dossier multi-jours"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>

            <h3 className="mb-4 text-lg font-medium text-gray-900">Déclencheurs</h3>
            <div className="rounded-lg border border-gray-100 bg-white p-5">
              <Trigger groupsOperator={groupsOperator} setGroupsOperator={setGroupsOperator} triggerGroups={triggerGroups} setTriggerGroups={setTriggerGroups} />
              <div className="mt-5 border-t border-gray-100 pt-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">Mode de déclenchement</label>
                <select
                  value={effectiveMode}
                  onChange={(e) => setMode(e.target.value)}
                  disabled={hasStatusTrigger}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
                >
                  <option value="edge">À chaque fois que les conditions deviennent vraies</option>
                  <option value="once">Une seule fois par dossier</option>
                </select>
                {hasStatusTrigger && (
                  <p className="mt-1 text-xs text-gray-500">Verrouillé sur « une seule fois par dossier » : un déclencheur « Statut du dossier » ne peut s'exécuter qu'une fois.</p>
                )}
              </div>
            </div>

            <h3 className="mb-4 mt-8 text-lg font-medium text-gray-900">Effets</h3>
            <div className="rounded-lg border border-gray-100 bg-white">
              {effects.map((effect, index) => {
                const effectConfig = EFFECT_TYPES.find((e) => e.value === effect.type);
                const linkedQuestion =
                  effect.type === "ANSWER_QUESTION" && effect.options?.answerType === "TEXT" && effect.options?.questionId
                    ? diagramData?.questions?.find((q) => q._id === effect.options.questionId)
                    : null;
                const questionOptions = (linkedQuestion?.options || []).map((opt) => ({
                  _id: opt._id,
                  code: opt.code,
                  value: opt.label || "",
                }));
                const filteredQuestionOptions = questionOptions.filter(
                  (opt) => !effect.options?.answer || opt.value.toLowerCase().includes(effect.options.answer.toLowerCase()),
                );
                return (
                  <div key={index} className="relative" {...getDragProps(index)}>
                    {showDropBefore(index) && <div className="absolute -top-px left-4 right-4 z-10 h-0.5 rounded-full bg-primary-500" />}
                    <div className={`transition-opacity ${isDragging(index) ? "opacity-50" : ""} ${index > 0 ? "border-t border-gray-100" : ""}`}>
                      <div className="flex items-center gap-2 px-3 py-2.5">
                        <MdDragIndicator className={`flex-shrink-0 text-base ${effects.length > 1 ? "cursor-grab text-gray-300 active:cursor-grabbing" : "text-gray-200"}`} />
                        <select
                          value={effect.type}
                          onChange={(e) => updateEffectType(index, e.target.value)}
                          className="min-w-0 flex-1 rounded-md border border-gray-200 bg-transparent px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        >
                          {EFFECT_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => removeEffect(index)}
                          className="flex-shrink-0 rounded-md p-1 text-gray-300 transition-colors hover:bg-red-50 hover:text-red-500"
                          title="Supprimer cet effet"
                        >
                          <HiOutlineTrash className="h-4 w-4" />
                        </button>
                      </div>
                      {TARGETABLE_TYPES.includes(effect.type) && getEarlierDuplicateNames(index).length > 0 && (
                        <div className="px-3 pb-2 pl-9">
                          <label className="mb-1 block text-xs font-medium text-gray-500">Cible</label>
                          <select
                            value={effect.target || ""}
                            onChange={(e) => updateEffectTarget(index, e.target.value)}
                            className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="">Dossier source</option>
                            {getEarlierDuplicateNames(index).map((name) => (
                              <option key={name} value={name}>
                                Duplicata : {name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {effectConfig?.hasValues && (
                        <div className="px-3 pb-3 pl-9">
                          {effect.type === "REMOVE_CODES_OF_TYPE" || effect.type === "REMOVE_ALL_CODES_EXCEPT_OF_TYPE" ? (
                            <>
                              <label className="mb-1 block text-xs font-medium text-gray-500">
                                {effect.type === "REMOVE_CODES_OF_TYPE" ? "Types d'examen à supprimer" : "Types d'examen à conserver"}
                              </label>
                              <ExamTypeSelect value={effect.values} onChange={(values) => updateEffectValues(index, values)} />
                            </>
                          ) : (
                            <>
                              {effect.type === "REPLACE_CODES" && <label className="mb-1 block text-xs font-medium text-gray-500">Codes à remplacer</label>}
                              {effect.type === "REMOVE_ALL_CODES_EXCEPT" && <label className="mb-1 block text-xs font-medium text-gray-500">Codes à conserver</label>}
                              <CodeLookup onChange={(tags) => updateEffectValues(index, tags.map((t) => t.value))} initialValues={effect.values} placeholder="Rechercher ou saisir des codes" />
                            </>
                          )}
                        </div>
                      )}
                      {effect.type === "REPLACE_CODES" && (
                        <div className="px-3 pb-3 pl-9">
                          <label className="mb-1 block text-xs font-medium text-gray-500">Codes de remplacement</label>
                          <CodeLookup
                            onChange={(tags) => setEffects(effects.map((ef, i) => (i === index ? { ...ef, options: { ...ef.options, replaceWith: tags.map((t) => t.value) } } : ef)))}
                            initialValues={effect.options?.replaceWith || []}
                            placeholder="Rechercher ou saisir des codes"
                          />
                        </div>
                      )}
                      {effect.type === "DUPLICATE_ADMISSION" && (
                        <div className="space-y-2 px-3 pb-3 pl-9">
                          <p className="flex items-start gap-1.5 text-xs italic text-gray-500">
                            <HiInformationCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
                            Recommandé : utilisez un déclencheur « Statut du dossier » réglé sur « En finalisation », avec le mode « une seule fois par dossier », pour ne dupliquer le dossier qu'une fois finalisé.
                          </p>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-500">
                              Nom du duplicata <span className="text-gray-400">(optionnel, à référencer par les effets suivants)</span>
                            </label>
                            <input
                              type="text"
                              value={effect.options?.duplicateName || ""}
                              onChange={(e) => updateEffectDuplicateName(index, e.target.value)}
                              placeholder="ex: dup-jour2"
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                            />
                          </div>
                          <label className="flex cursor-pointer items-center gap-2.5">
                            <input
                              type="checkbox"
                              checked={effect.options?.keepAnswers === true}
                              onChange={(e) => setEffects(effects.map((ef, i) => (i === index ? { ...ef, options: { ...ef.options, keepAnswers: e.target.checked } } : ef)))}
                              className="h-3.5 w-3.5 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-600">Conserver les réponses aux questions</span>
                          </label>
                          <label className="flex cursor-pointer items-center gap-2.5">
                            <input
                              type="checkbox"
                              checked={effect.options?.followsParent === true}
                              onChange={(e) => setEffects(effects.map((ef, i) => (i === index ? { ...ef, options: { ...ef.options, followsParent: e.target.checked } } : ef)))}
                              className="h-3.5 w-3.5 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-600">Envoyer en saisie en même temps que le dossier source</span>
                          </label>
                        </div>
                      )}
                      {effect.type === "ANSWER_QUESTION" && (
                        <div className="space-y-3 px-3 pb-3 pl-9">
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-500">Question</label>
                            <QuestionLookup
                              initialValue={effect.options?.questionId || null}
                              excludeAnswerTypes={["DOCUMENT"]}
                              onChange={(question) => {
                                setEffects(
                                  effects.map((ef, i) =>
                                    i === index
                                      ? { ...ef, options: { ...ef.options, questionId: question?._id || "", answer: "", answerType: question?.answerType || "" } }
                                      : ef,
                                  ),
                                );
                              }}
                              placeholder="Rechercher une question..."
                            />
                          </div>
                          {effect.options?.questionId && (
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-500">Réponse</label>
                              {effect.options?.answerType === "MULTIPLE_CHOICE" ? (
                                <AnswerLookup
                                  key={effect.options?.answer || "empty"}
                                  questionId={effect.options.questionId}
                                  initialValue={effect.options?.answer ? [effect.options.answer] : []}
                                  onChange={(tags) => {
                                    const selectedAnswer = tags.length > 0 ? tags[tags.length - 1]._id : "";
                                    setEffects(effects.map((ef, i) => (i === index ? { ...ef, options: { ...ef.options, answer: selectedAnswer } } : ef)));
                                  }}
                                  placeholder="Sélectionner une réponse..."
                                />
                              ) : effect.options?.answerType === "TEXT" ? (
                                <div className="relative">
                                  <input
                                    type="text"
                                    value={effect.options?.answer || ""}
                                    onChange={(e) => {
                                      setEffects(effects.map((ef, i) => (i === index ? { ...ef, options: { ...ef.options, answer: e.target.value } } : ef)));
                                      if (questionOptions.length > 0) {
                                        setOpenAutocompleteIndex(index);
                                        setSelectedSuggestionIndex(0);
                                      }
                                    }}
                                    onFocus={() => {
                                      if (questionOptions.length > 0) {
                                        setOpenAutocompleteIndex(index);
                                        setSelectedSuggestionIndex(0);
                                      }
                                    }}
                                    onBlur={() => {
                                      if (questionOptions.length > 0) {
                                        setTimeout(() => {
                                          setOpenAutocompleteIndex(null);
                                          setSelectedSuggestionIndex(0);
                                        }, 200);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (questionOptions.length > 0 && openAutocompleteIndex === index) {
                                        if (e.key === "ArrowDown") {
                                          e.preventDefault();
                                          setSelectedSuggestionIndex((prev) => (prev < filteredQuestionOptions.length - 1 ? prev + 1 : prev));
                                        } else if (e.key === "ArrowUp") {
                                          e.preventDefault();
                                          setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : 0));
                                        } else if (e.key === "Enter" && filteredQuestionOptions.length > 0) {
                                          e.preventDefault();
                                          const selectedOption = filteredQuestionOptions[selectedSuggestionIndex];
                                          if (selectedOption) {
                                            setEffects(effects.map((ef, i) => (i === index ? { ...ef, options: { ...ef.options, answer: selectedOption.value } } : ef)));
                                            setOpenAutocompleteIndex(null);
                                            setSelectedSuggestionIndex(0);
                                          }
                                        } else if (e.key === "Escape") {
                                          setOpenAutocompleteIndex(null);
                                          setSelectedSuggestionIndex(0);
                                        }
                                      }
                                    }}
                                    placeholder="Saisir la réponse..."
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  />
                                  {questionOptions.length > 0 && openAutocompleteIndex === index && filteredQuestionOptions.length > 0 && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto">
                                      {filteredQuestionOptions.map((option, sugIndex) => (
                                        <button
                                          key={sugIndex}
                                          type="button"
                                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                                            sugIndex === selectedSuggestionIndex ? "bg-gray-100" : ""
                                          }`}
                                          onMouseDown={(e) => {
                                            e.preventDefault();
                                            setEffects(effects.map((ef, i) => (i === index ? { ...ef, options: { ...ef.options, answer: option.value } } : ef)));
                                            setOpenAutocompleteIndex(null);
                                            setSelectedSuggestionIndex(0);
                                          }}
                                          onMouseEnter={() => setSelectedSuggestionIndex(sugIndex)}
                                        >
                                          {option.value}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <input
                                  type={effect.options?.answerType === "DATE" ? "date" : "time"}
                                  value={effect.options?.answer || ""}
                                  onChange={(e) => setEffects(effects.map((ef, i) => (i === index ? { ...ef, options: { ...ef.options, answer: e.target.value } } : ef)))}
                                  placeholder={effect.options?.answerType === "DATE" ? "YYYY-MM-DD" : "HH:mm"}
                                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {showDropAfter(index, effects.length) && <div className="absolute -bottom-px left-4 right-4 z-10 h-0.5 rounded-full bg-primary-500" />}
                  </div>
                );
              })}
              <div className={effects.length > 0 ? "border-t border-gray-100" : ""}>
                <button
                  type="button"
                  onClick={addEffect}
                  className="flex w-full items-center justify-center gap-1.5 px-3 py-2.5 text-sm text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
                >
                  <HiOutlinePlus className="h-3.5 w-3.5" />
                  Ajouter un effet
                </button>
              </div>
            </div>

          </div>
        </div>

        <div className="col-span-2 shrink-0 overflow-y-auto border-l border-gray-200 bg-white p-6">
          <h3 className="mb-4 text-lg font-medium text-gray-900">Dépendances</h3>
          {diagramData ? (
            <DependencyExplorer
              data={{
                questions: diagramData.questions,
                guidelines: diagramData.guidelines,
                actions: [...(diagramData.actions || []), { _id: "new-action", name: name || "Nouvelle action", triggerGroups, groupsOperator, effects }],
              }}
              currentActionId="new-action"
              className="rounded-lg border border-gray-100"
            />
          ) : (
            <div className="flex items-center justify-center rounded-lg border border-gray-100 py-8 text-sm text-gray-400">Chargement du diagramme...</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddAction;
